import { getActiveTimezone } from '@/utils/timezone';
import { useQuery } from '@tanstack/react-query';
import { customerOrderApi, CustomerOrderStatus } from '@/services/customerOrderApi';
import { X, Phone, MessageCircle, ExternalLink, Clock } from 'lucide-react';
import { getCurrencySymbol, getActiveCurrency } from '@/utils/currency';

const STATUS_COLORS: Record<CustomerOrderStatus, string> = {
  NEW: 'bg-blue-100 text-blue-700',
  ORDERED: 'bg-yellow-100 text-yellow-700',
  RECEIVED: 'bg-green-100 text-green-700',
  NOTIFIED: 'bg-purple-100 text-purple-700',
  DELIVERED: 'bg-emerald-100 text-emerald-700',
  CANCELLED: 'bg-red-100 text-red-700',
};

const STATUS_LABELS: Record<CustomerOrderStatus, string> = {
  NEW: 'New',
  ORDERED: 'Ordered',
  RECEIVED: 'Received',
  NOTIFIED: 'Notified',
  DELIVERED: 'Delivered',
  CANCELLED: 'Cancelled',
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  orderId: string;
}

export function OrderDetailModal({ isOpen, onClose, orderId }: Props) {
  const { data: order, isLoading } = useQuery({
    queryKey: ['customer-order', orderId],
    queryFn: () => customerOrderApi.getById(orderId),
    enabled: isOpen && !!orderId,
  });

  if (!isOpen) return null;

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: getActiveTimezone() });
  };

  const formatCurrency = (val: number | null) => {
    if (val === null || val === undefined) return '-';
    return `${getCurrencySymbol()}${Number(val).toLocaleString(getActiveCurrency().locale)}`;
  };

  const getItemDisplay = () => {
    if (!order) return '-';
    return order.brand && order.model
      ? `${order.brand.name} ${order.model.name}`
      : order.itemName || '-';
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold text-gray-900">Order Details</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : !order ? (
          <div className="p-8 text-center text-gray-500">Order not found</div>
        ) : (
          <div className="p-5 space-y-5">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xl font-bold text-gray-900">{order.orderNumber}</span>
                <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${STATUS_COLORS[order.status]}`}>
                  {STATUS_LABELS[order.status]}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-500">Customer</p>
                  <p className="font-medium">{order.customer.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Phone className="w-3 h-3 text-gray-400" />
                    <span className="text-gray-600">{order.customer.phone}</span>
                  </div>
                </div>
                <div>
                  <p className="text-gray-500">Device / Item</p>
                  <p className="font-medium">{getItemDisplay()}</p>
                  <p className="text-gray-600">Qty: {order.quantity}</p>
                </div>
                <div>
                  <p className="text-gray-500">Expected Price</p>
                  <p className="font-medium">{formatCurrency(order.expectedPrice)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Advance Paid</p>
                  <p className="font-medium">{formatCurrency(order.advanceAmount)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Branch</p>
                  <p className="font-medium">{order.branch?.name || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Created By</p>
                  <p className="font-medium">{order.createdBy.name}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-gray-500">Created</p>
                  <p className="font-medium">{formatDate(order.createdAt)}</p>
                </div>
              </div>

              {order.notes && (
                <div>
                  <p className="text-sm text-gray-500">Notes</p>
                  <p className="text-sm text-gray-700 mt-0.5">{order.notes}</p>
                </div>
              )}

              {order.internalNotes && (
                <div>
                  <p className="text-sm text-gray-500">Internal Notes</p>
                  <p className="text-sm text-gray-700 mt-0.5">{order.internalNotes}</p>
                </div>
              )}

              {order.cancelReason && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-600 font-medium">Cancel Reason</p>
                  <p className="text-sm text-red-700 mt-0.5">{order.cancelReason}</p>
                </div>
              )}
            </div>

            {order.whatsappNotifiedAt && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <MessageCircle className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium text-green-700">WhatsApp Notified</span>
                </div>
                <p className="text-sm text-green-600">
                  {formatDate(order.whatsappNotifiedAt)}
                  {order.whatsappNotifier && ` by ${order.whatsappNotifier.name}`}
                </p>
              </div>
            )}

            {order.service && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-purple-700">Linked Service</p>
                    <p className="text-sm text-purple-600">{order.service.ticketNumber}</p>
                  </div>
                  <ExternalLink className="w-4 h-4 text-purple-500" />
                </div>
              </div>
            )}

            {order.statusHistory && order.statusHistory.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Status History</h3>
                <div className="space-y-3">
                  {order.statusHistory.map((entry, idx) => (
                    <div key={entry.id} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className={`w-2.5 h-2.5 rounded-full mt-1.5 ${
                          idx === order.statusHistory!.length - 1 ? 'bg-purple-500' : 'bg-gray-300'
                        }`} />
                        {idx < order.statusHistory!.length - 1 && (
                          <div className="w-px h-full bg-gray-200 mt-1" />
                        )}
                      </div>
                      <div className="pb-3">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_COLORS[entry.status]}`}>
                            {STATUS_LABELS[entry.status]}
                          </span>
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(entry.createdAt)}
                          </span>
                        </div>
                        {entry.changedByUser && (
                          <p className="text-xs text-gray-500 mt-0.5">by {entry.changedByUser.name}</p>
                        )}
                        {entry.notes && (
                          <p className="text-xs text-gray-600 mt-0.5">{entry.notes}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
